import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, Clock, AlertTriangle, Calendar, Briefcase, 
  ChevronRight, Target, FileText, MessageSquare, Bell,
  Users, TrendingUp, Zap, Plus, Loader2, Eye, AlertCircle,
  BarChart3, Settings, RefreshCw, Send, X, Star, Video
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

// Modal Component - defined outside
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const AEAdminDashboard = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showPartnershipModal, setShowPartnershipModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Form states
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', due_date: '' });
  const [contentForm, setContentForm] = useState({ title: '', description: '', platform: 'instagram', content_type: 'post', series: '' });
  const [campaignForm, setCampaignForm] = useState({ title: '', description: '', start_date: '', end_date: '', budget: '' });
  const [partnershipForm, setPartnershipForm] = useState({ company_name: '', contact_name: '', contact_email: '', deal_type: 'brand_deal', notes: '' });
  const [requestForm, setRequestForm] = useState({ title: '', description: '', request_type: 'general', priority: 'medium', due_date: '' });
  const [eventForm, setEventForm] = useState({ title: '', description: '', event_type: 'appearance', location: '', event_date: '' });

  useEffect(() => {
    if (userEmail) {
      fetchDashboard();
    }
  }, [userEmail]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/dashboard/admin?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setDashboard(data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create handlers
  const handleCreateTask = async () => {
    if (!taskForm.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...taskForm, status: 'pending' })
      });
      const data = await res.json();
      if (data.success) {
        setShowTaskModal(false);
        setTaskForm({ title: '', description: '', priority: 'medium', due_date: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating task:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContent = async () => {
    if (!contentForm.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/content?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contentForm, status: 'draft' })
      });
      const data = await res.json();
      if (data.success) {
        setShowContentModal(false);
        setContentForm({ title: '', description: '', platform: 'instagram', content_type: 'post', series: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating content:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/projects?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          title: campaignForm.title,
          description: campaignForm.description,
          division: 'personal',
          priority: 'high',
          start_date: campaignForm.start_date,
          deadline: campaignForm.end_date,
          pillar: 'campaign'
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowCampaignModal(false);
        setCampaignForm({ title: '', description: '', start_date: '', end_date: '', budget: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePartnership = async () => {
    if (!partnershipForm.company_name) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/partnerships?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...partnershipForm,
          status: 'prospecting'
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowPartnershipModal(false);
        setPartnershipForm({ company_name: '', contact_name: '', contact_email: '', deal_type: 'brand_deal', notes: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating partnership:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!requestForm.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/client-requests?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm)
      });
      const data = await res.json();
      if (data.success) {
        setShowRequestModal(false);
        setRequestForm({ title: '', description: '', request_type: 'general', priority: 'medium', due_date: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating request:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/events?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...eventForm,
          status: 'upcoming'
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowEventModal(false);
        setEventForm({ title: '', description: '', event_type: 'appearance', location: '', event_date: '' });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error creating event:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  const { kpis = {}, internal_review = [], awaiting_client = [], overdue = [], recent_activity = [], pipeline = [] } = dashboard || {};

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6" data-testid="admin-header">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ELLE MAXWELL AGENCY</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>
                Admin Command Center
              </h1>
              <p className="text-gray-400 text-sm mt-1">Akayleb Evans Brand Management</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={fetchDashboard}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors relative">
                <Bell className="w-5 h-5" />
                {kpis.overdue_tasks > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                    {kpis.overdue_tasks}
                  </span>
                )}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg hover:bg-[#D4B062] transition-colors font-medium">
                <Send className="w-4 h-4" />
                Send to Akayleb
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* KPIs Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8" data-testid="kpis-section">
          <div className="ae-card-glow ae-card-glow-blue p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400" />
              {kpis.overdue_tasks > 0 && <AlertCircle className="w-4 h-4 text-red-400" />}
            </div>
            <p className="text-2xl font-bold">{kpis.open_tasks || 0}</p>
            <p className="text-xs text-gray-400">Open Tasks</p>
            {kpis.overdue_tasks > 0 && (
              <p className="text-xs text-red-400 mt-1">{kpis.overdue_tasks} overdue</p>
            )}
          </div>

          <div className="ae-card-glow p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-[#C4A052]" />
            </div>
            <p className="text-2xl font-bold text-[#C4A052]">{kpis.client_approvals_pending || 0}</p>
            <p className="text-xs text-gray-400">Client Approvals Pending</p>
          </div>

          <div className="ae-card-glow ae-card-glow-green p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-2xl font-bold">{kpis.content_scheduled || 0}</p>
            <p className="text-xs text-gray-400">Content Scheduled</p>
          </div>

          <div className="ae-card-glow ae-card-glow-purple p-4">
            <div className="flex items-center justify-between mb-2">
              <Briefcase className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-2xl font-bold">{kpis.active_prospects || 0}</p>
            <p className="text-xs text-gray-400">Active Prospects</p>
          </div>

          <div className="ae-card-glow ae-card-glow-cyan p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-cyan-400" />
            </div>
            <p className="text-2xl font-bold">{kpis.upcoming_appearances || 0}</p>
            <p className="text-xs text-gray-400">Upcoming Appearances</p>
          </div>
        </div>

        {/* Quick Create Buttons */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="quick-create">
          <button 
            onClick={() => setShowTaskModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4" /> New Task
          </button>
          <button 
            onClick={() => setShowContentModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="new-content-btn"
          >
            <Plus className="w-4 h-4" /> New Content
          </button>
          <button 
            onClick={() => setShowCampaignModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="new-campaign-btn"
          >
            <Plus className="w-4 h-4" /> New Campaign
          </button>
          <button 
            onClick={() => setShowPartnershipModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="new-partnership-btn"
          >
            <Plus className="w-4 h-4" /> New Partnership
          </button>
          <button 
            onClick={() => setShowRequestModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="client-request-btn"
          >
            <Plus className="w-4 h-4" /> Client Request
          </button>
          <button 
            onClick={() => setShowEventModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="new-event-btn"
          >
            <Plus className="w-4 h-4" /> New Event
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Internal Review Queue */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="internal-review">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/20">
                  <Eye className="w-5 h-5 text-yellow-400" />
                </div>
                <h2 className="font-semibold">Internal Review</h2>
              </div>
              <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">
                {internal_review.length}
              </span>
            </div>
            <div className="divide-y divide-white/5 max-h-[350px] overflow-y-auto">
              {internal_review.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No items in review</p>
                </div>
              ) : (
                internal_review.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-400 mt-1">{item.platform} • {item.series}</p>
                      </div>
                      <button className="text-xs px-2 py-1 bg-[#C4A052]/20 text-[#C4A052] rounded hover:bg-[#C4A052]/30 transition-colors">
                        Review
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Awaiting Akayleb */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="awaiting-client">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#C4A052]/20">
                  <Clock className="w-5 h-5 text-[#C4A052]" />
                </div>
                <h2 className="font-semibold">Awaiting Akayleb</h2>
              </div>
              <span className="text-xs px-2 py-1 bg-[#C4A052]/20 text-[#C4A052] rounded-full">
                {awaiting_client.length}
              </span>
            </div>
            <div className="divide-y divide-white/5 max-h-[350px] overflow-y-auto">
              {awaiting_client.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nothing awaiting client</p>
                </div>
              ) : (
                awaiting_client.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-400 mt-1">Sent {new Date(item.sent_at).toLocaleDateString()}</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-[#C4A052]/20 text-[#C4A052] rounded">
                        Pending
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Overdue Items */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="overdue-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <h2 className="font-semibold text-red-400">Overdue</h2>
              </div>
              <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">
                {overdue.length}
              </span>
            </div>
            <div className="divide-y divide-white/5 max-h-[350px] overflow-y-auto">
              {overdue.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" />
                  <p className="text-sm text-green-400">All caught up!</p>
                </div>
              ) : (
                overdue.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{item.title}</h4>
                        <p className="text-xs text-red-400 mt-1">
                          {item.due_date ? `Due ${new Date(item.due_date).toLocaleDateString()}` : 'No due date set'}
                        </p>
                      </div>
                      <button className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors">
                        Resolve
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Lower Grid - Pipeline & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Partnership Pipeline */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="pipeline-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Briefcase className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-semibold">Partnership Pipeline</h2>
              </div>
              <a href="/ae/admin/partnerships" className="text-xs text-[#C4A052] hover:underline">View All</a>
            </div>
            <div className="divide-y divide-white/5">
              {pipeline.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No active prospects</p>
                </div>
              ) : (
                pipeline.map((stage, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 transition-colors flex items-center justify-between">
                    <span className="text-sm">{stage.status?.replace(/_/g, ' ') || stage._id?.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-bold text-[#C4A052]">{stage.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Client Activity */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="activity-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-semibold">Recent Client Activity</h2>
              </div>
            </div>
            <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
              {recent_activity.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                recent_activity.map((activity, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-500/20">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">{activity.action?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* New Task Modal */}
      <Modal show={showTaskModal} onClose={() => setShowTaskModal(false)} title="New Task">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Title *</label>
            <input
              type="text"
              value={taskForm.title}
              onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Task title"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={taskForm.description}
              onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Task description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Priority</label>
              <select
                value={taskForm.priority}
                onChange={(e) => setTaskForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
              <input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowTaskModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreateTask} disabled={!taskForm.title || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Task
          </button>
        </div>
      </Modal>

      {/* New Content Modal */}
      <Modal show={showContentModal} onClose={() => setShowContentModal(false)} title="New Content">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Title *</label>
            <input
              type="text"
              value={contentForm.title}
              onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Content title"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={contentForm.description}
              onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Content description or script"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Platform</label>
              <select
                value={contentForm.platform}
                onChange={(e) => setContentForm(prev => ({ ...prev, platform: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
                <option value="twitter">Twitter/X</option>
                <option value="linkedin">LinkedIn</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Content Type</label>
              <select
                value={contentForm.content_type}
                onChange={(e) => setContentForm(prev => ({ ...prev, content_type: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="post">Post</option>
                <option value="story">Story</option>
                <option value="reel">Reel</option>
                <option value="video">Video</option>
                <option value="carousel">Carousel</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Series (optional)</label>
            <input
              type="text"
              value={contentForm.series}
              onChange={(e) => setContentForm(prev => ({ ...prev, series: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="e.g., Film Room, Beyond the Game"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowContentModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreateContent} disabled={!contentForm.title || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Content
          </button>
        </div>
      </Modal>

      {/* New Campaign Modal */}
      <Modal show={showCampaignModal} onClose={() => setShowCampaignModal(false)} title="New Campaign">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Campaign Name *</label>
            <input
              type="text"
              value={campaignForm.title}
              onChange={(e) => setCampaignForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Campaign name"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={campaignForm.description}
              onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Campaign description and goals"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Start Date</label>
              <input
                type="date"
                value={campaignForm.start_date}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">End Date</label>
              <input
                type="date"
                value={campaignForm.end_date}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Budget (optional)</label>
            <input
              type="text"
              value={campaignForm.budget}
              onChange={(e) => setCampaignForm(prev => ({ ...prev, budget: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="e.g., $5,000"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowCampaignModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreateCampaign} disabled={!campaignForm.title || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Campaign
          </button>
        </div>
      </Modal>

      {/* New Partnership Modal */}
      <Modal show={showPartnershipModal} onClose={() => setShowPartnershipModal(false)} title="New Partnership">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Company Name *</label>
            <input
              type="text"
              value={partnershipForm.company_name}
              onChange={(e) => setPartnershipForm(prev => ({ ...prev, company_name: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Company or brand name"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Contact Name</label>
              <input
                type="text"
                value={partnershipForm.contact_name}
                onChange={(e) => setPartnershipForm(prev => ({ ...prev, contact_name: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                placeholder="Primary contact"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Contact Email</label>
              <input
                type="email"
                value={partnershipForm.contact_email}
                onChange={(e) => setPartnershipForm(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                placeholder="email@company.com"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Deal Type</label>
            <select
              value={partnershipForm.deal_type}
              onChange={(e) => setPartnershipForm(prev => ({ ...prev, deal_type: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
            >
              <option value="brand_deal">Brand Deal</option>
              <option value="sponsorship">Sponsorship</option>
              <option value="endorsement">Endorsement</option>
              <option value="appearance">Appearance</option>
              <option value="partnership">Partnership</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Notes</label>
            <textarea
              value={partnershipForm.notes}
              onChange={(e) => setPartnershipForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Additional notes about this opportunity"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowPartnershipModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreatePartnership} disabled={!partnershipForm.company_name || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Partnership
          </button>
        </div>
      </Modal>

      {/* Client Request Modal */}
      <Modal show={showRequestModal} onClose={() => setShowRequestModal(false)} title="New Client Request">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Title *</label>
            <input
              type="text"
              value={requestForm.title}
              onChange={(e) => setRequestForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Request title"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={requestForm.description}
              onChange={(e) => setRequestForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Describe the request"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Type</label>
              <select
                value={requestForm.request_type}
                onChange={(e) => setRequestForm(prev => ({ ...prev, request_type: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="content">Content</option>
                <option value="availability">Availability</option>
                <option value="approval">Approval</option>
                <option value="feedback">Feedback</option>
                <option value="general">General</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Priority</label>
              <select
                value={requestForm.priority}
                onChange={(e) => setRequestForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
            <input
              type="date"
              value={requestForm.due_date}
              onChange={(e) => setRequestForm(prev => ({ ...prev, due_date: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowRequestModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreateRequest} disabled={!requestForm.title || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Request
          </button>
        </div>
      </Modal>

      {/* New Event Modal */}
      <Modal show={showEventModal} onClose={() => setShowEventModal(false)} title="New Event">
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Event Name *</label>
            <input
              type="text"
              value={eventForm.title}
              onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Event name"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={eventForm.description}
              onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
              placeholder="Event description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Event Type</label>
              <select
                value={eventForm.event_type}
                onChange={(e) => setEventForm(prev => ({ ...prev, event_type: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              >
                <option value="appearance">Appearance</option>
                <option value="interview">Interview</option>
                <option value="photoshoot">Photoshoot</option>
                <option value="meeting">Meeting</option>
                <option value="game">Game</option>
                <option value="charity">Charity</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Event Date</label>
              <input
                type="date"
                value={eventForm.event_date}
                onChange={(e) => setEventForm(prev => ({ ...prev, event_date: e.target.value }))}
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Location</label>
            <input
              type="text"
              value={eventForm.location}
              onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
              className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
              placeholder="Event location"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowEventModal(false)} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">Cancel</button>
          <button onClick={handleCreateEvent} disabled={!eventForm.title || saving} className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Event
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AEAdminDashboard;
